import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle, XCircle, Ghost, Calendar, Shield, Search, AlertTriangle, ArrowLeft, Loader2, FileText, Download, Eye, Star, MapPin, Globe, User, Mail, Phone, Linkedin, Info, Target, UserCheck, Building2, AlertOctagon, ShieldCheck, FileSearch, MessageSquareWarning, BadgeCheck, CircleDollarSign, Fingerprint, MailCheck, PhoneCall, SearchCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
interface VerificationReport {
  scorecard: {
    confidenceScore: number;
    riskLevel: 'low' | 'medium' | 'high';
    classification: 'Real' | 'Suspicious' | 'Fraudulent';
    verdict: string;
  };
  summary: string;
  reportHeader: any;
  jobOverview: any;
  aiSummaryVerdict: string;
  classificationFlags: Record<string, {
    result: string;
    notes: string;
  }>;
  departmentalInsights: any;
  salaryBenchmark: any;
  redFlags: string[];
  applicationRecommendations: any;
  auditLog: Array<{
    stepChecked: string;
    platform: string;
    criteria: string;
    outcome: string;
  }>;
}
const JobCheck = () => {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const [jobUrl, setJobUrl] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [report, setReport] = useState<VerificationReport | null>(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [pendingReportId, setPendingReportId] = useState<string | null>(null);
  const [pollingMessage, setPollingMessage] = useState('');
  const [activeTab, setActiveTab] = useState('job');

  // Recruiter verification state
  const [recruiterName, setRecruiterName] = useState('');
  const [recruiterCompany, setRecruiterCompany] = useState('');
  const [recruiterLinkedIn, setRecruiterLinkedIn] = useState('');
  const [recruiterEmail, setRecruiterEmail] = useState('');
  const [recruiterPhone, setRecruiterPhone] = useState('');
  const [suspiciousMessages, setSuspiciousMessages] = useState('');
  const [isAnalyzingRecruiter, setIsAnalyzingRecruiter] = useState('');
  const hasValidRecruiterInput = recruiterName.trim() || recruiterLinkedIn.trim() || recruiterEmail.trim();
  // Dummy report data for design preview
  const dummyReport = {
    reportHeader: {
      dateGenerated: "Friday, September 19, 2025, 4:03 AM CDT",
      reportVersion: "Standard",
      legitimacyScore: 87,
      overallVerdict: "Real Job Posting"
    },
    jobOverview: {
      jobTitle: "Key Account Sales Manager",
      companyName: "SafeTech",
      jobLocation: "Humble, TX",
      jobSource: "Multiple platforms (Indeed, LinkedIn, BeBee, Learn4Good)",
      companyWebsite: "www.safetech-usa.com",
      jobDescriptionPreview: "Aviation MRO industry sales role requiring 10+ years experience managing key customer accounts for aircraft safety equipment services"
    },
    classification: {
      type: "Real",
      description: "This is a legitimate job posting from an established aviation safety equipment company. SafeTech is a verified business with proper certifications, physical locations, and a detailed professional job description that matches their industry expertise."
    },
    scorecard: {
      confidenceScore: 87,
      riskLevel: "low" as const,
      classification: "Real" as const,
      verdict: "High-confidence legitimate opportunity from verified aviation MRO company with detailed technical requirements."
    },
    aiSummaryVerdict: "The job posting receives a high legitimacy score due to SafeTech being a verified aviation safety equipment company founded in 1984 and part of the SK AeroSafety Group. The company maintains FAA and EASA certifications, operates multiple repair stations across the USA, and has an established physical presence in Humble, TX. The job description demonstrates industry expertise with specific technical requirements for aviation MRO experience, regulatory knowledge, and realistic travel expectations. The posting appears on multiple legitimate job platforms without suspicious redirect patterns, and the company's website provides comprehensive business information and contact details.",
    classificationCriteria: [{
      category: "Company Legitimacy",
      score: "Verified",
      notes: "Established 1984, FAA/EASA certified, part of SK AeroSafety Group",
      status: "✅"
    }, {
      category: "Recruiter Identity",
      score: "Limited",
      notes: "No specific recruiter identified, posted through job boards",
      status: "✅"
    }, {
      category: "Job Description Quality",
      score: "High",
      notes: "Detailed technical requirements, industry-specific qualifications",
      status: "✅"
    }, {
      category: "Contact Info Present",
      score: "Partial",
      notes: "Company contact info available, no recruiter details provided",
      status: "✅"
    }, {
      category: "Application Safety",
      score: "Secure",
      notes: "Applications through legitimate job boards, no suspicious redirects",
      status: "✅"
    }, {
      category: "Known Scam Patterns",
      score: "None",
      notes: "No red flags detected, realistic requirements and compensation",
      status: "✅"
    }],
    redFlags: ["No significant red flags detected. The company has mixed employee reviews mentioning management issues and high turnover, but this indicates a real workplace rather than a scam operation."],
    applicationRecommendations: {
      recommendedAction: "Apply",
      safetyGuidance: "Apply through the official job board links provided (Indeed, LinkedIn). Research company culture through employee reviews before interviewing.",
      additionalNotes: "Verify specific role details and compensation during interview process, as employee reviews suggest potential workplace culture concerns."
    },
    securityReminders: "This report is based on available information and AI analysis. Users should apply discretion and validate independently. Never share SSNs, passport scans, or bank information during any application process.",
    reportSummary: "The JobCheck™ Legitimacy Report helps protect your time and personal information by determining whether a job listing is worth pursuing. This analysis is designed to help you focus only on real, active, and legitimate opportunities."
  };
  const hasValidInput = jobDescription.trim() || jobUrl.trim();

  // Poll for report completion
  const pollForReport = async (startTime: Date) => {
    const maxPollTime = 10 * 60 * 1000; // 10 minutes max
    const pollInterval = 5000; // 5 seconds

    const poll = async (): Promise<boolean> => {
      const elapsed = Date.now() - startTime.getTime();
      if (elapsed > maxPollTime) {
        throw new Error('Verification timed out. Please try again.');
      }

      // Update polling message
      const minutes = Math.floor(elapsed / 60000);
      const seconds = Math.floor(elapsed % 60000 / 1000);
      setPollingMessage(`Analyzing job posting... ${minutes}:${seconds.toString().padStart(2, '0')}`);

      // Check for completed report
      const {
        data: reports,
        error
      } = await supabase.from('legitimacy_reports').select('*').eq('user_id', (await supabase.auth.getUser()).data.user?.id).eq('status', 'completed').gte('created_at', startTime.toISOString()).order('created_at', {
        ascending: false
      }).limit(1);
      if (error) {
        console.error('Error polling for report:', error);
        return false;
      }
      if (reports && reports.length > 0) {
        const dbReport = reports[0];
        const reportText = typeof dbReport.report === 'string' ? dbReport.report : JSON.stringify(dbReport.report || '');
        const redFlagsArr = Array.isArray(dbReport.red_flags) ? dbReport.red_flags as string[] : [];
        // Transform database report to VerificationReport format
        const transformedReport: VerificationReport = {
          scorecard: {
            confidenceScore: 75,
            riskLevel: (dbReport.risk_level as 'low' | 'medium' | 'high') || 'medium',
            classification: (dbReport.classification as 'Real' | 'Suspicious' | 'Fraudulent') || 'Suspicious',
            verdict: dbReport.hiring_status || 'Analysis completed'
          },
          summary: reportText.substring(0, 500) || 'Job posting analysis completed',
          reportHeader: {
            dateGenerated: new Date().toISOString(),
            userSubmissionTimestamp: dbReport.created_at,
            reportVersion: '1.0',
            reviewer: 'JobCheck™ AI Verification System'
          },
          jobOverview: {
            jobTitle: dbReport.job_title || 'Not specified',
            companyName: dbReport.company_name || 'Not specified',
            jobUrls: dbReport.job_url ? [dbReport.job_url] : [],
            jobDescriptionPreview: reportText.substring(0, 200) || ''
          },
          aiSummaryVerdict: reportText || 'Analysis completed',
          classificationFlags: {},
          departmentalInsights: {},
          salaryBenchmark: {},
          redFlags: redFlagsArr,
          applicationRecommendations: {
            recommendedAction: dbReport.classification === 'LEGITIMATE' || dbReport.classification === 'Real' ? 'Apply' : 'Proceed with caution',
            safetySteps: ['Research company independently', 'Verify job posting authenticity']
          },
          auditLog: [{
            stepChecked: 'JobCheck™ Analysis',
            platform: 'TailorWiz',
            criteria: 'Comprehensive job posting analysis',
            outcome: 'Completed'
          }]
        };
        setReport(transformedReport);
        return true;
      }

      // Continue polling
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      return poll();
    };
    return poll();
  };
  const handleAnalyze = async () => {
    if (!hasValidInput) {
      toast({
        title: "Missing Information",
        description: "Please provide either a job URL or job description to analyze.",
        variant: "destructive"
      });
      return;
    }
    setIsAnalyzing(true);
    setPollingMessage('Starting analysis...');
    const startTime = new Date();
    try {
      // Trigger the async job verification
      const {
        data,
        error
      } = await supabase.functions.invoke('trigger-job-verification', {
        body: {
          job_description: jobDescription.trim() || undefined,
          job_url: jobUrl.trim() || undefined
        }
      });
      if (error) {
        throw error;
      }
      if (!data.success) {
        throw new Error(data.error || 'Failed to start analysis');
      }
      toast({
        title: "Analysis Started",
        description: "JobCheck™ is analyzing the job posting. This may take 2-5 minutes."
      });

      // Poll for results
      const found = await pollForReport(startTime);
      if (found) {
        toast({
          title: "Analysis Complete",
          description: "JobCheck™ verification report generated successfully."
        });
      }
    } catch (error) {
      console.error('Job verification error:', error);
      toast({
        title: "Analysis Failed",
        description: error instanceof Error ? error.message : "Unable to complete job verification. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
      setPollingMessage('');
    }
  };
  const getRiskBadge = (riskLevel: string) => {
    switch (riskLevel) {
      case 'low':
        return <Badge className="bg-primary/10 text-primary">🟢 Low Risk</Badge>;
      case 'medium':
        return <Badge className="bg-warning/10 text-warning">🟠 Medium Risk</Badge>;
      case 'high':
        return <Badge className="bg-destructive/10 text-destructive">🔴 High Risk</Badge>;
      default:
        return <Badge variant="secondary">Unknown Risk</Badge>;
    }
  };
  const getClassificationBadge = (classification: string) => {
    switch (classification) {
      case 'Real':
        return <Badge className="bg-primary/10 text-primary">✅ Real</Badge>;
      case 'Suspicious':
        return <Badge className="bg-warning/10 text-warning">⚠️ Suspicious</Badge>;
      case 'Fraudulent':
        return <Badge className="bg-destructive/10 text-destructive">❌ Fraudulent</Badge>;
      default:
        return <Badge variant="secondary">Unverified</Badge>;
    }
  };
  return <div className="min-h-screen bg-emerald-50/50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" onClick={() => navigate('/jobintel-360-reports')} className="bg-white">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports Hub
          </Button>
          <div className="flex items-center gap-4 flex-1">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">JobCheck™</h1>
              <p className="text-muted-foreground mt-1">Discover if a job opportunity, company or recruiter is legit in just minutes!</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/job-legitimacy-reports')} className="bg-white border-emerald-300 hover:border-emerald-400">
              <FileText className="h-4 w-4 mr-2" />
              My Reports
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {/* Hero Section */}
          <Card className="border-2 border-emerald-400 bg-emerald-50 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="space-y-6">
                <div>
                  <h1 className="text-4xl font-bold text-foreground mb-4">
                    Instantly Verify Job & Recruiter Legitimacy
                  </h1>
                  <p className="text-lg text-muted-foreground max-w-3xl mx-auto px-[59px]">
                    Nearly half of job postings on major boards are fake, ghosted, or expired—and over <span className="font-bold text-destructive">400,000 criminals</span> are posing as recruiters online right now, waiting to scam desperate job seekers. Stop wasting time on dead leads and dangerous traps. <span className="font-bold text-foreground">Verify before you apply.</span>
                  </p>
                  
                  {/* Two Links Side by Side */}
                  <div className="flex items-center justify-center gap-6 mt-3">
                    {/* Why You Need This Link */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <button className="inline-flex items-center gap-1.5 text-amber-600 hover:text-amber-700 font-semibold underline underline-offset-4 decoration-2 decoration-amber-400 hover:decoration-amber-600 transition-all cursor-pointer">
                          <AlertTriangle className="w-4 h-4" />
                          Why you need this
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
                        <DialogHeader className="px-6 pt-6 pb-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-md">
                              <AlertTriangle className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <DialogTitle className="text-2xl font-bold text-foreground">Job & Recruiter Legitimacy Report</DialogTitle>
                              <p className="text-muted-foreground mt-1">Why Every Job Seeker Needs Protection</p>
                            </div>
                          </div>
                        </DialogHeader>
                        <ScrollArea className="max-h-[70vh] px-6 py-4">
                          <div className="space-y-6">
                            {/* Introduction */}
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                              <p className="text-foreground leading-relaxed">
                                The Job Legitimacy Report is designed to <span className="font-semibold">protect your time, energy, and personal information</span> by analyzing whether a job posting is real and worth pursuing.
                              </p>
                            </div>

                            {/* 5 Categories */}
                            <div className="space-y-3">
                              <h3 className="text-lg font-bold text-foreground">Job Classification Categories</h3>
                              <div className="grid gap-2">
                                <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <span className="font-semibold text-emerald-700">Real</span>
                                    <p className="text-sm text-muted-foreground">Active and legitimate opportunity</p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                  <Ghost className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <span className="font-semibold text-gray-700">Ghost/Fake</span>
                                    <p className="text-sm text-muted-foreground">Listed but not actively hiring or never intended to be filled</p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                  <Calendar className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <span className="font-semibold text-amber-700">Expired</span>
                                    <p className="text-sm text-muted-foreground">Once open but now closed or outdated</p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                  <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <span className="font-semibold text-red-700">Scam</span>
                                    <p className="text-sm text-muted-foreground">Fraudulent with malicious intent (phishing, money theft)</p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                  <AlertTriangle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <span className="font-semibold text-purple-700">Misleading</span>
                                    <p className="text-sm text-muted-foreground">Commission-only, MLM, or disguised business opportunities</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <Separator />

                            {/* Statistics Table */}
                            <div className="space-y-3">
                              <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                                <Target className="w-5 h-5 text-amber-600" />
                                The Reality of Online Job Postings
                              </h3>
                              <div className="overflow-hidden rounded-lg border border-gray-200">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="px-4 py-3 text-left font-semibold text-foreground">Category</th>
                                      <th className="px-4 py-3 text-left font-semibold text-foreground">Prevalence</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    <tr className="bg-emerald-50">
                                      <td className="px-4 py-3 font-medium text-emerald-700">Real</td>
                                      <td className="px-4 py-3 text-muted-foreground">~60–70% of postings</td>
                                    </tr>
                                    <tr className="bg-gray-50">
                                      <td className="px-4 py-3 font-medium text-gray-700">Ghost / Fake</td>
                                      <td className="px-4 py-3 text-muted-foreground">18–40% of postings</td>
                                    </tr>
                                    <tr className="bg-amber-50">
                                      <td className="px-4 py-3 font-medium text-amber-700">Expired</td>
                                      <td className="px-4 py-3 text-muted-foreground">~15–20% of postings</td>
                                    </tr>
                                    <tr className="bg-red-50">
                                      <td className="px-4 py-3 font-medium text-red-700">Scam</td>
                                      <td className="px-4 py-3 text-muted-foreground">1–5% of postings</td>
                                    </tr>
                                    <tr className="bg-purple-50">
                                      <td className="px-4 py-3 font-medium text-purple-700">Misleading</td>
                                      <td className="px-4 py-3 text-muted-foreground">Frequent in sales roles</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              <p className="text-sm text-muted-foreground italic">
                                Sources: Gizmodo, Fortune, ResumeBuilder, The Guardian, Wikipedia
                              </p>
                            </div>

                            {/* Key Insights */}
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                              <h4 className="font-bold text-red-800">Key Insights</h4>
                              <ul className="space-y-2 text-sm text-foreground">
                                <li className="flex items-start gap-2">
                                  <span className="text-red-500 font-bold">•</span>
                                  <span><strong>Ghost/Fake is the biggest risk</strong> — up to 1 in 5 to 2 in 5 jobs online fall here</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-500 font-bold">•</span>
                                  <span>Expired jobs add another 15–20%, meaning <strong>~1/3 of online job postings may be a waste of time</strong></span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-500 font-bold">•</span>
                                  <span>Scam + Misleading combined (~5–10%) carry the <strong>highest personal risk</strong></span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-emerald-500 font-bold">•</span>
                                  <span>Only about <strong>60–70% of online postings are truly "Real & Active"</strong></span>
                                </li>
                              </ul>
                            </div>

                            <Separator />

                            {/* Company Challenges Section */}
                            <div className="space-y-4">
                              <h3 className="text-lg font-bold text-foreground">Key Company Challenges & Focus Areas (2025)</h3>
                              
                              <div className="space-y-3">
                                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                  <h4 className="font-semibold text-blue-800 mb-1">1. Hiring Slowing & HR Caution</h4>
                                  <p className="text-sm text-muted-foreground">1 in 5 U.S. employers plan to slow hiring in the second half of 2025—almost double last year's rate—as economic and policy uncertainty mounts.</p>
                                </div>
                                
                                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                                  <h4 className="font-semibold text-indigo-800 mb-1">2. Talent Acquisition Struggles</h4>
                                  <ul className="text-sm text-muted-foreground space-y-1">
                                    <li>• 56% of organizations cite attracting and retaining skilled talent as a top challenge</li>
                                    <li>• 60% of companies report their time-to-hire increased in 2024</li>
                                    <li>• 70% of job seekers say it's harder to find quality roles</li>
                                  </ul>
                                </div>
                                
                                <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
                                  <h4 className="font-semibold text-violet-800 mb-1">3. Leadership & Culture Development</h4>
                                  <ul className="text-sm text-muted-foreground space-y-1">
                                    <li>• 75% of HR leaders say managers are overwhelmed</li>
                                    <li>• 70% believe their leadership development programs are not future-ready</li>
                                    <li>• 73% of employees are experiencing change fatigue</li>
                                  </ul>
                                </div>
                              </div>
                            </div>

                            {/* Why This Matters */}
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
                              <h4 className="font-bold text-emerald-800">Why This Matters to Job Seekers</h4>
                              <p className="text-sm text-foreground">
                                Understanding what a company is struggling with—and what it prioritizes—gives job seekers a strategic advantage:
                              </p>
                              <ul className="text-sm text-muted-foreground space-y-2">
                                <li className="flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                  <span>If leadership development is under pressure, tailor your resume to stress mentoring, coaching, or cross-functional leadership</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                  <span>Where hiring efficiency is a gap, emphasize adaptability, process improvement, or fast-paced experience</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                  <span>For tech-focused organizations, showcase experience with automation tools and AI-savvy workflows</span>
                                </li>
                              </ul>
                            </div>

                            {/* Our Analysis */}
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                              <p className="text-foreground">
                                Our analysis scans for <span className="font-semibold">dozens of signals</span> including company legitimacy, job description quality, application process safety, posting age, recruiter activity, and known scam red flags. The outcome gives you a <span className="font-semibold">clear confidence score and practical verdict</span>—helping you focus only on active, safe opportunities.
                              </p>
                            </div>
                          </div>
                        </ScrollArea>
                      </DialogContent>
                    </Dialog>

                    <span className="text-muted-foreground">|</span>

                    {/* Learn How We Protect You Link */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <button className="inline-flex items-center gap-1.5 text-emerald-600 hover:text-emerald-700 font-semibold underline underline-offset-4 decoration-2 decoration-emerald-400 hover:decoration-emerald-600 transition-all cursor-pointer">
                          <Info className="w-4 h-4" />
                          Learn how we protect you →
                        </button>
                      </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] p-0">
                      <DialogHeader className="px-6 pt-6 pb-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-md">
                            <Shield className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <DialogTitle className="text-2xl font-bold text-foreground">JobIntel 360 Legitimacy Report</DialogTitle>
                            <p className="text-muted-foreground mt-1">Two-Part Scam & Ghost Job Detection System</p>
                          </div>
                        </div>
                      </DialogHeader>
                      <ScrollArea className="max-h-[70vh] px-6 py-4">
                        <div className="space-y-6">
                          {/* Introduction */}
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <p className="text-foreground leading-relaxed">
                              The JobIntel 360 Legitimacy Report is a <span className="font-semibold">two-part scam and ghost job detection system</span> that protects job seekers from wasting time on fake, expired, or misleading opportunities.
                            </p>
                          </div>

                          {/* Option 1 */}
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                                <Target className="w-4 h-4 text-emerald-600" />
                              </div>
                              <h3 className="text-lg font-bold text-foreground">Option 1: Job Verification</h3>
                            </div>
                            <p className="text-muted-foreground pl-10">
                              Investigates whether a job posting is legitimate by cross-referencing the company's official careers page, verifying the company actually exists (domain age, LinkedIn presence, BBB listing), searching for scam warnings and fraud reports, and checking whether the employer is actively hiring—detecting ghost jobs that will never be filled, phantom listings fabricated by aggregator sites, MLM schemes disguised as employment, and outright scams designed to steal money or personal information.
                            </p>
                          </div>

                          {/* Option 2 */}
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                <UserCheck className="w-4 h-4 text-purple-600" />
                              </div>
                              <h3 className="text-lg font-bold text-foreground">Option 2: Recruiter/Contact Verification</h3>
                            </div>
                            <p className="text-muted-foreground pl-10">
                              Investigates the person who reached out to you—verifying their identity, confirming they actually work at the company they claim, checking their LinkedIn history and email domain, and searching for scam reports tied to their name.
                            </p>
                          </div>

                          {/* Value Statement */}
                          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                            <p className="text-foreground font-medium">
                              Whether you found a job posting online or a recruiter slid into your DMs, this report tells you if the opportunity is real and if the person behind it is who they say they are—<span className="text-emerald-700">backed by evidence, not assumptions</span>—so you know exactly what you're dealing with before you invest time or share personal information.
                            </p>
                          </div>

                          <Separator />

                          {/* What You'll Learn Header */}
                          <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                            <FileSearch className="w-5 h-5 text-emerald-600" />
                            What You'll Learn
                          </h3>

                          {/* Job & Company Verification Section */}
                          <div className="space-y-4">
                            <div className="flex items-center gap-2 bg-emerald-100 px-3 py-2 rounded-lg">
                              <Building2 className="w-5 h-5 text-emerald-700" />
                              <h4 className="font-bold text-emerald-800">Option 1: Job & Company Verification</h4>
                            </div>
                            
                            <div className="grid gap-3 pl-2">
                              <div className="flex gap-3">
                                <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Legitimacy Verdict</span>
                                  <p className="text-sm text-muted-foreground">Clear classification: Legitimate, Likely Legitimate, Ghost Job, Phantom/Synthetic Listing, Misleading, Likely Scam, or Confirmed Scam—with evidence-based reasoning</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <AlertOctagon className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Risk Level Assessment</span>
                                  <p className="text-sm text-muted-foreground">Low, Medium, High, or Critical risk rating based on red flag severity</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <Search className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Active Hiring Status</span>
                                  <p className="text-sm text-muted-foreground">Is the company actually filling this role right now, or is this a stale posting? Based on Glassdoor interview activity, recruiter engagement, layoff news, and posting freshness</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <Globe className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Official Careers Page Verification</span>
                                  <p className="text-sm text-muted-foreground">Does this exact job exist on the company's official careers site, or only on aggregators? The #1 indicator of ghost and phantom jobs</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <Building2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Company Verification</span>
                                  <p className="text-sm text-muted-foreground">Official website check, domain age via WHOIS, LinkedIn company page and employee count, BBB listing, and physical presence</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <MessageSquareWarning className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Scam Warning Search</span>
                                  <p className="text-sm text-muted-foreground">Reddit, Glassdoor, BBB complaints, FTC warnings, and whether the company has issued official fraud alerts about fake jobs</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Aggregator Red Flag Detection</span>
                                  <p className="text-sm text-muted-foreground">Is this posting on a known problematic aggregator with fabricated job titles or outdated listings?</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <BadgeCheck className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Application Method Safety Check</span>
                                  <p className="text-sm text-muted-foreground">Is the application going through a legitimate ATS (Workday, Greenhouse, Lever) or suspicious channels (Google Forms, WhatsApp, personal email)?</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <FileText className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Job Description Analysis</span>
                                  <p className="text-sm text-muted-foreground">Scam indicators (payment requests, urgency tactics, too-good-to-be-true pay) and MLM red flags</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <CircleDollarSign className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Salary Reality Check</span>
                                  <p className="text-sm text-muted-foreground">Is the posted compensation realistic for the role, or suspiciously high to lure victims?</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Recruiter/Contact Verification Section */}
                          <div className="space-y-4">
                            <div className="flex items-center gap-2 bg-purple-100 px-3 py-2 rounded-lg">
                              <UserCheck className="w-5 h-5 text-purple-700" />
                              <h4 className="font-bold text-purple-800">Option 2: Recruiter/Contact Verification</h4>
                            </div>
                            
                            <div className="grid gap-3 pl-2">
                              <div className="flex gap-3">
                                <Fingerprint className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Identity Verification</span>
                                  <p className="text-sm text-muted-foreground">Does this person exist on LinkedIn with a legitimate profile history, or is this a recently created account with few connections?</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <Building2 className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Employment Confirmation</span>
                                  <p className="text-sm text-muted-foreground">Do they actually work at the company they claim to represent, and does their LinkedIn employment history match?</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <MailCheck className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Email Domain Check</span>
                                  <p className="text-sm text-muted-foreground">Is their email from the company's official domain, or are they using Gmail/Yahoo while claiming to represent a corporation?</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <PhoneCall className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Contact Method Red Flags</span>
                                  <p className="text-sm text-muted-foreground">Are they pushing communication through WhatsApp, Telegram, or text instead of professional channels? Do they refuse video calls?</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <SearchCheck className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Scam Report Search</span>
                                  <p className="text-sm text-muted-foreground">Have other job seekers reported this recruiter or similar outreach patterns as fraudulent?</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Behavioral Red Flags</span>
                                  <p className="text-sm text-muted-foreground">Are they pushy about collecting personal information before an interview, or evasive about basic details?</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Combined Assessment */}
                          <div className="space-y-4">
                            <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                              <Target className="w-5 h-5 text-slate-700" />
                              <h4 className="font-bold text-slate-800">Combined Assessment</h4>
                            </div>
                            
                            <div className="grid gap-3 pl-2">
                              <div className="flex gap-3">
                                <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Red Flags Summary</span>
                                  <p className="text-sm text-muted-foreground">Every concern itemized with specific evidence and why it matters</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-3">
                                <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold text-foreground">Actionable Recommendation</span>
                                  <p className="text-sm text-muted-foreground">Exactly what to do next: apply with confidence, proceed with caution, skip entirely, or report to the FTC</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </ScrollArea>
                    </DialogContent>
                  </Dialog>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
                  <div className="flex flex-col items-center space-y-2 p-4 bg-red-50 border-2 border-red-400 rounded-lg">
                    <XCircle className="w-8 h-8 text-destructive" />
                    <span className="font-semibold">Fake Jobs</span>
                    <span className="text-sm text-muted-foreground text-center">Scams & data harvesting</span>
                  </div>
                  <div className="flex flex-col items-center space-y-2 p-4 bg-gray-50 border-2 border-gray-400 rounded-lg">
                    <Ghost className="w-8 h-8 text-muted-foreground" />
                    <span className="font-semibold">Ghost Jobs</span>
                    <span className="text-sm text-muted-foreground text-center">Never intended to fill</span>
                  </div>
                  <div className="flex flex-col items-center space-y-2 p-4 bg-amber-50 border-2 border-amber-400 rounded-lg">
                    <Calendar className="w-8 h-8 text-amber-500" />
                    <span className="font-semibold">Expired Jobs</span>
                    <span className="text-sm text-muted-foreground text-center">Long closed, still listed</span>
                  </div>
                  <div className="flex flex-col items-center space-y-2 p-4 bg-purple-50 border-2 border-purple-400 rounded-lg">
                    <User className="w-8 h-8 text-purple-500" />
                    <span className="font-semibold">Fake Recruiters</span>
                    <span className="text-sm text-muted-foreground text-center">Identity theft & fraud</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs for Job vs Recruiter Verification */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-emerald-100 p-1 h-auto">
              <TabsTrigger value="job" className="py-3 border-2 border-transparent data-[state=inactive]:border-blue-400 data-[state=inactive]:bg-white data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-sm font-semibold">
                <Shield className="w-4 h-4 mr-2" />
                Check Job Legitimacy
              </TabsTrigger>
              <TabsTrigger value="recruiter" className="py-3 border-2 border-transparent data-[state=inactive]:border-blue-400 data-[state=inactive]:bg-white data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-sm font-semibold">
                <User className="w-4 h-4 mr-2" />
                Check Recruiter or HR Rep Legitimacy
              </TabsTrigger>
            </TabsList>

            {/* Job Legitimacy Tab */}
            <TabsContent value="job">
              <Card className="border-2 border-emerald-400 bg-white shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader className="bg-emerald-50 border-b border-emerald-200">
                  <CardTitle className="flex items-center text-xl">
                    <Search className="w-6 h-6 mr-3 text-emerald-600" />
                    Check Your Job Now
                  </CardTitle>
                  <CardDescription>
                    Enter a job URL <strong>or</strong> paste the job description to get an instant legitimacy analysis
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-8 space-y-6">
                  <div>
                    <Label htmlFor="jobUrl" className="text-base font-semibold">Job URL</Label>
                    <Input id="jobUrl" placeholder="https://linkedin.com/jobs/view/12345 or https://indeed.com/job/..." value={jobUrl} onChange={e => setJobUrl(e.target.value)} className="mt-2 border-2 border-gray-400" />
                    <p className="text-sm text-muted-foreground mt-2">
                      Paste a direct link to the job posting from LinkedIn, Indeed, or any job board.
                    </p>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">Or</span>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="jobDescription" className="text-base font-semibold">Job Description</Label>
                    <Textarea id="jobDescription" placeholder="Paste the full job description here (including job title, company name, requirements, etc.)..." rows={8} value={jobDescription} onChange={e => setJobDescription(e.target.value)} className="min-h-[200px] mt-2 border-2 border-gray-400" />
                    <p className="text-sm text-muted-foreground mt-2">
                      Include the complete job posting with title, company, responsibilities, and requirements for best results.
                    </p>
                  </div>
                  
                  <div className="flex gap-4 pt-4">
                    <Button onClick={handleAnalyze} className="flex-1 text-lg py-6 bg-emerald-600 hover:bg-emerald-700" disabled={!hasValidInput || isAnalyzing}>
                      {isAnalyzing ? <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {pollingMessage || 'Analyzing Job...'}
                        </> : <>
                          <Shield className="w-5 h-5 mr-2" />
                          Run JobCheck™ Analysis
                        </>}
                    </Button>
                    
                    <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="text-lg py-6 px-6">
                          <Eye className="w-5 h-5 mr-2" />
                          Preview Report
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 gap-0">
                        <DialogHeader className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-b">
                          <DialogTitle className="flex items-center text-2xl font-bold">
                            <Shield className="w-7 h-7 mr-3 text-emerald-600" />
                            JobCheck™ Legitimacy Report
                            <Badge className="ml-auto bg-emerald-600 text-white">VERIFIED</Badge>
                          </DialogTitle>
                        </DialogHeader>
                    
                    <ScrollArea className="flex-1 p-0">
                      <div className="p-6 space-y-8">
                        {/* Hero Score Section */}
                        <Card className="overflow-hidden">
                          <div className="bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 p-8 text-center">
                            <div className="inline-flex items-center justify-center w-32 h-32 bg-white/90 rounded-full shadow-lg mb-6">
                              <div className="text-center">
                                <div className="text-4xl font-bold text-primary">
                                  {dummyReport.reportHeader.legitimacyScore}
                                </div>
                                <div className="text-sm font-medium text-muted-foreground">/100</div>
                              </div>
                            </div>
                            
                            <Badge className="bg-primary text-white text-lg px-6 py-2 mb-4 shadow-lg">
                              <CheckCircle className="w-5 h-5 mr-2" />
                              {dummyReport.classification.type} Job Posting
                            </Badge>
                            
                            <div className="grid grid-cols-3 gap-6 max-w-md mx-auto">
                              <div className="text-center">
                                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                                  <Shield className="w-6 h-6 text-primary" />
                                </div>
                                <div className="text-sm font-medium">Confidence</div>
                                <div className="text-lg font-bold text-primary">High</div>
                              </div>
                              <div className="text-center">
                                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                                  <CheckCircle className="w-6 h-6 text-primary" />
                                </div>
                                <div className="text-sm font-medium">Risk Level</div>
                                <div className="text-lg font-bold text-primary">Low</div>
                              </div>
                              <div className="text-center">
                                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                                  <Star className="w-6 h-6 text-primary" />
                                </div>
                                <div className="text-sm font-medium">Status</div>
                                <div className="text-lg font-bold text-primary">Verified</div>
                              </div>
                            </div>
                          </div>
                        </Card>

                        {/* Job Details Card */}
                        <Card>
                          <CardHeader className="bg-muted/50">
                            <CardTitle className="flex items-center text-xl">
                              <FileText className="w-6 h-6 mr-3 text-primary" />
                              Job Details
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                              <div className="space-y-4">
                                <div className="flex items-start space-x-3 p-4 bg-primary/5 rounded-lg">
                                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-5 h-5 text-primary" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-muted-foreground">Job Title</div>
                                    <div className="text-lg font-bold">{dummyReport.jobOverview.jobTitle}</div>
                                  </div>
                                </div>
                                
                                <div className="flex items-start space-x-3 p-4 bg-accent/5 rounded-lg">
                                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Shield className="w-5 h-5 text-accent" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-muted-foreground">Company</div>
                                    <div className="text-lg font-bold">{dummyReport.jobOverview.companyName}</div>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="space-y-4">
                                <div className="flex items-start space-x-3 p-4 bg-muted/30 rounded-lg">
                                  <div className="w-10 h-10 bg-muted/50 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <MapPin className="w-5 h-5 text-muted-foreground" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-muted-foreground">Location</div>
                                    <div className="text-lg font-semibold">{dummyReport.jobOverview.jobLocation}</div>
                                  </div>
                                </div>
                                
                                <div className="flex items-start space-x-3 p-4 bg-muted/30 rounded-lg">
                                  <div className="w-10 h-10 bg-muted/50 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Globe className="w-5 h-5 text-muted-foreground" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-muted-foreground">Website</div>
                                    <div className="text-lg font-semibold text-primary cursor-pointer hover:underline">
                                      {dummyReport.jobOverview.companyWebsite}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="p-6 bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl border border-primary/10">
                              <div className="text-sm font-semibold mb-3 flex items-center">
                                <Eye className="w-4 h-4 mr-2 text-primary" />
                                Job Description Preview
                              </div>
                              <p className="text-sm leading-relaxed text-muted-foreground">
                                {dummyReport.jobOverview.jobDescriptionPreview}
                              </p>
                            </div>
                          </CardContent>
                        </Card>

                        {/* AI Analysis Card */}
                        <Card>
                          <CardHeader className="bg-gradient-to-r from-accent/10 to-primary/10">
                            <CardTitle className="flex items-center text-xl">
                              <Star className="w-6 h-6 mr-3 text-accent" />
                              AI Analysis Summary
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-6">
                            <div className="p-6 bg-gradient-to-r from-accent/5 to-primary/5 rounded-xl border border-accent/20">
                              <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                                  <Star className="w-6 h-6 text-accent" />
                                </div>
                                <p className="text-sm leading-relaxed">{dummyReport.aiSummaryVerdict}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Verification Criteria Card */}
                        <Card>
                          <CardHeader className="bg-muted/50">
                            <CardTitle className="flex items-center text-xl">
                              <CheckCircle className="w-6 h-6 mr-3 text-primary" />
                              Verification Criteria
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-6">
                            <div className="grid gap-4">
                              {dummyReport.classificationCriteria.map((criteria, index) => <div key={index} className="flex items-center justify-between p-4 bg-primary/5 rounded-lg border border-primary/10 hover:bg-primary/10 transition-colors">
                                  <div className="flex items-center space-x-4">
                                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                      <CheckCircle className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="font-semibold text-lg">{criteria.category}</div>
                                      <div className="text-sm text-muted-foreground">{criteria.notes}</div>
                                    </div>
                                  </div>
                                  <Badge className="bg-primary text-primary-foreground font-bold px-3 py-1">
                                    {criteria.score}
                                  </Badge>
                                </div>)}
                            </div>
                          </CardContent>
                        </Card>

                        {/* Security Assessment Card */}
                        <Card>
                          <CardHeader className="bg-primary/5">
                            <CardTitle className="flex items-center text-xl">
                              <Shield className="w-6 h-6 mr-3 text-primary" />
                              Security Assessment
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-6">
                            <div className="p-6 bg-primary/5 rounded-xl border border-primary/20">
                              <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                  <CheckCircle className="w-6 h-6 text-primary" />
                                </div>
                                <div className="flex-1">
                                  <div className="font-semibold text-lg text-primary mb-2">No Red Flags Detected</div>
                                  <p className="text-sm leading-relaxed">{dummyReport.redFlags[0]}</p>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Recommendations Card */}
                        <Card>
                          <CardHeader className="bg-gradient-to-r from-primary/10 to-accent/10">
                            <CardTitle className="flex items-center text-xl">
                              <Star className="w-6 h-6 mr-3 text-primary" />
                              Application Recommendations
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-6">
                            <div className="space-y-6">
                              <div className="text-center p-6 bg-primary/10 rounded-xl">
                                <Badge className="bg-primary text-primary-foreground text-lg px-6 py-3 mb-4">
                                  <CheckCircle className="w-5 h-5 mr-2" />
                                  Recommended Action: {dummyReport.applicationRecommendations.recommendedAction}
                                </Badge>
                              </div>
                              
                              <div className="grid gap-4">
                                <div className="p-5 bg-accent/5 rounded-lg border border-accent/20">
                                  <div className="flex items-start space-x-3">
                                    <Shield className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
                                    <div>
                                      <div className="font-semibold text-lg mb-2">Safety Guidance</div>
                                      <p className="text-sm leading-relaxed">{dummyReport.applicationRecommendations.safetyGuidance}</p>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="p-5 bg-muted/30 rounded-lg border">
                                  <div className="flex items-start space-x-3">
                                    <AlertTriangle className="w-6 h-6 text-muted-foreground mt-1 flex-shrink-0" />
                                    <div>
                                      <div className="font-semibold text-lg mb-2">Additional Notes</div>
                                      <p className="text-sm leading-relaxed">{dummyReport.applicationRecommendations.additionalNotes}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Security Reminders */}
                        <Card className="border-destructive/20">
                          <CardContent className="p-6">
                            <div className="p-6 bg-destructive/5 rounded-xl border border-destructive/20">
                              <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
                                  <Shield className="w-6 h-6 text-destructive" />
                                </div>
                                <div className="flex-1">
                                  <div className="font-semibold text-lg text-destructive mb-2">Security Reminders</div>
                                  <p className="text-sm leading-relaxed">{dummyReport.securityReminders}</p>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Footer */}
                        <div className="text-center p-6 bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl border">
                          <p className="text-sm text-muted-foreground italic leading-relaxed mb-4">
                            {dummyReport.reportSummary}
                          </p>
                          <div className="flex gap-3 justify-center">
                            <Button className="px-8 py-3">
                              <Download className="w-4 h-4 mr-2" />
                              Download Report
                             </Button>
                             <Button variant="outline" onClick={() => setShowReportDialog(false)} className="px-8 py-3">
                               Close Report
                             </Button>
                           </div>
                         </div>
                       </div>
                     </ScrollArea>
                   </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recruiter/HR Rep Legitimacy Tab */}
        <TabsContent value="recruiter">
          <Card className="border-2 border-emerald-400 bg-white shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="bg-emerald-50 border-b border-emerald-200">
              <CardTitle className="flex items-center text-xl">
                <User className="w-6 h-6 mr-3 text-emerald-600" />
                Verify Recruiter or HR Representative
              </CardTitle>
              <CardDescription>
                Enter recruiter details to verify their legitimacy before sharing personal information
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="recruiterName" className="text-base font-semibold flex items-center gap-2">
                    <User className="w-4 h-4 text-emerald-600" />
                    Recruiter Name
                  </Label>
                  <Input id="recruiterName" placeholder="e.g., John Smith" value={recruiterName} onChange={e => setRecruiterName(e.target.value)} className="mt-2 border-2 border-gray-400" />
                </div>

                <div>
                  <Label htmlFor="recruiterCompany" className="text-base font-semibold flex items-center gap-2">
                    <Globe className="w-4 h-4 text-emerald-600" />
                    Company Name
                  </Label>
                  <Input id="recruiterCompany" placeholder="e.g., ABC Recruiting Agency" value={recruiterCompany} onChange={e => setRecruiterCompany(e.target.value)} className="mt-2 border-2 border-gray-400" />
                </div>

                <div>
                  <Label htmlFor="recruiterLinkedIn" className="text-base font-semibold flex items-center gap-2">
                    <Linkedin className="w-4 h-4 text-emerald-600" />
                    LinkedIn Profile URL
                  </Label>
                  <Input id="recruiterLinkedIn" placeholder="https://linkedin.com/in/johnsmith" value={recruiterLinkedIn} onChange={e => setRecruiterLinkedIn(e.target.value)} className="mt-2 border-2 border-gray-400" />
                </div>

                <div>
                  <Label htmlFor="recruiterEmail" className="text-base font-semibold flex items-center gap-2">
                    <Mail className="w-4 h-4 text-emerald-600" />
                    Email Address
                  </Label>
                  <Input id="recruiterEmail" placeholder="recruiter@company.com" value={recruiterEmail} onChange={e => setRecruiterEmail(e.target.value)} className="mt-2 border-2 border-gray-400" />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="recruiterPhone" className="text-base font-semibold flex items-center gap-2">
                    <Phone className="w-4 h-4 text-emerald-600" />
                    Phone Number <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input id="recruiterPhone" placeholder="+1 (555) 123-4567" value={recruiterPhone} onChange={e => setRecruiterPhone(e.target.value)} className="mt-2 border-2 border-gray-400" />
                </div>
              </div>

              <Separator />

              <div>
                <Label htmlFor="suspiciousMessages" className="text-base font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Suspicious Messages or Communication <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Textarea id="suspiciousMessages" placeholder="Paste any messages or emails from the recruiter that seem suspicious or concerning..." rows={6} value={suspiciousMessages} onChange={e => setSuspiciousMessages(e.target.value)} className="min-h-[150px] mt-2 border-2 border-gray-400" />
                <p className="text-sm text-muted-foreground mt-2">
                  Include any red flags like requests for money, unusual urgency, or vague job details.
                </p>
              </div>

              <div className="flex gap-4 pt-4">
                <Button className="flex-1 text-lg py-6 bg-emerald-600 hover:bg-emerald-700" disabled={!hasValidRecruiterInput}>
                  <User className="w-5 h-5 mr-2" />
                  Verify Recruiter
                </Button>
              </div>

              {/* Warning Card */}
              <Card className="border-amber-300 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-amber-800">Protect Yourself from Recruitment Scams</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Never pay fees to apply for jobs, share SSN/bank info early, or accept checks before starting work. 
                        Legitimate recruiters won't ask for sensitive information upfront.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

        </div>
      </div>
    </div>;
};
export default JobCheck;